import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MenuFoldOutlined, MenuUnfoldOutlined, LockOutlined } from "@ant-design/icons";
import { Alert, Tag } from "antd";

import { useAuth } from "@/utils/auth.jsx";
import { useLockedFeature } from "@components/LockedFeature.jsx";
import { useTopUp } from "@/utils/useTopUp.js";
import { TopUpModal } from "@components/TopUp.jsx";
import { useMobileNav } from "@/utils/MobileNavContext.jsx";
import { USER_NAV_GROUPS, ADMIN_NAV_GROUPS } from "@/assets/data/navConfig.js";

const ADMIN_UID = "U4mlColHvRdwiA0q6jU13cuMExB3";

const badgeColor = (badge) => (badge === "new" ? "magenta" : "blue");

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { mobileOpen, closeMobileNav } = useMobileNav();
  const { openLocked, lockedModal } = useLockedFeature();
  const {
    isModalOpen,
    openModal,
    closeModal,
    handleWithdrawal,
    success,
    error,
    clearSuccess,
    clearError,
  } = useTopUp();

  const isAdmin = user.uid === ADMIN_UID;
  const groups = isAdmin ? ADMIN_NAV_GROUPS : USER_NAV_GROUPS;

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname]);

  const renderIcon = (Icon) => (Icon ? <Icon /> : null);

  const renderSubItem = (item) => {
    if (item.locked) {
      return (
        <a key={item.key} className="nav-link nav-link--locked" href="#" onClick={openLocked}>
          <div className="nav-link__icon">{renderIcon(item.icon)}</div>
          {!collapsed && <span className="nav-link__label">{item.label}</span>}
          {!collapsed && item.badge && (
            <Tag className="nav-link__badge" color={badgeColor(item.badge)}>
              {item.badge}
            </Tag>
          )}
          {!collapsed && <LockOutlined className="nav-link__lock" />}
        </a>
      );
    }

    if (item.action === "topup") {
      return (
        <a
          key={item.key}
          className="nav-link"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            openModal();
          }}
        >
          <div className="nav-link__icon">{renderIcon(item.icon)}</div>
          {!collapsed && <span className="nav-link__label">{item.label}</span>}
        </a>
      );
    }

    return (
      <NavLink key={item.key} to={item.to} className="nav-link">
        <div className="nav-link__icon">{renderIcon(item.icon)}</div>
        {!collapsed && <span className="nav-link__label">{item.label}</span>}
      </NavLink>
    );
  };

  const renderGroup = (group) => {
    // Standalone entry with no sub-items
    if (!group.items) {
      if (group.locked) {
        return (
          <a key={group.key} className="nav-link nav-link--locked nav-link--top" href="#" onClick={openLocked}>
            <div className="nav-link__icon">{renderIcon(group.icon)}</div>
            {!collapsed && <span className="nav-link__label">{group.label}</span>}
            {!collapsed && <LockOutlined className="nav-link__lock" />}
          </a>
        );
      }
      return (
        <NavLink key={group.key} to={group.to} className="nav-link nav-link--top">
          <div className="nav-link__icon">{renderIcon(group.icon)}</div>
          {!collapsed && <span className="nav-link__label">{group.label}</span>}
        </NavLink>
      );
    }

    return (
      <div key={group.key} className="nav-group">
        {!collapsed && (
          <div className="nav-group__title">
            {renderIcon(group.icon)}
            <span>{group.label}</span>
          </div>
        )}
        <div className="nav-group__items">{group.items.map(renderSubItem)}</div>
      </div>
    );
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={closeMobileNav} />}
      <aside
        className={`sidebar${collapsed ? " sidebar--collapsed" : ""}${mobileOpen ? " sidebar--mobile-open" : ""}`}
      >
        <div className="sidebar-header">
          <button className="sidebar-collapse" onClick={() => setCollapsed((prev) => !prev)}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </div>

        <nav>{groups.map(renderGroup)}</nav>

        {success && (
          <Alert message="Withdrawal request created and pending approval" type="success" showIcon closable onClose={clearSuccess} />
        )}
        {error && (
          <Alert message="Failed to create withdrawal request!" type="error" showIcon closable onClose={clearError} />
        )}

        <TopUpModal open={isModalOpen} onCancel={closeModal} onSubmit={handleWithdrawal} />
        {lockedModal}
      </aside>
    </>
  );
};

export default Sidebar;
